import { LitElement, html, css } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import dayjs from 'dayjs';
import { actions, selectors } from './';
import { connect } from '../store/connect';
import { selectors as accountSelectors } from '../account';
import { getRouter } from '../routing/route-base';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-select';
import '@trumedianetworks/tmn-core-components/src/tmn-switch';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@trumedianetworks/tmn-core-components/src/tmn-input';
import { SELECT_MODES } from '../table/table-constants';
import '../routing/routed-link';
import '../table/tmn-table-searchable';
import '../table/tmn-table-selectable';

const dateFormatFn = (value, row) => {
    return value
        ? html`${dayjs(value).format('YYYY-MM-DD')}`
        : html`<div style="text-align:center">-</div>`;
};
class UserSearchAll extends LitElement {
    constructor() {
        super();
        this.allusers = [];
        this._columnDefs = [];
    }

    static get styles() {
        // can be shared with site or more general table component maybe?
        return [
            css`
                tmn-switch {
                    white-space: nowrap;
                }
                .user-inactive {
                    color: rgba(0, 0, 0, 0.1);
                }
                .user-active {
                    color: var(--app-primary-color);
                }
                .table-container {
                    height: calc(100% - 255px);
                }
            `,
        ];
    }

    static get properties() {
        return {
            allusers: {
                type: Array,
            },
            _columnDefs: {
                type: Array,
            },
            authorizations: {
                type: Array,
            },
            showInactive: {
                type: Boolean,
            },
            search: {
                type: String,
            },
        };
    }

    updated(changedProps) {
        // do i need this? - yes or else we need to define column defs outside of the component
        if (changedProps.has('authorizations')) {
            const { authorizations } = this;
            this._columnDefs = [
                {
                    key: 'userId',
                    label: 'Id',
                    sortable: true,
                    search: false, // todo need to make this work as a string in search
                },
                {
                    label: 'Name',
                    key: 'display_name',
                    sortable: true,
                    search: true,
                },
                {
                    key: 'user_type',
                    label: 'Type',
                    sortable: true,
                },
                {
                    key: 'username',
                    label: 'Username',
                    sortable: true,
                    search: true,
                },
                {
                    key: 'sitename',
                    label: 'Site',
                    sortable: true,
                },
                {
                    key: 'expiration_date',
                    label: 'Expiration',
                    sortable: true,
                    dataFormatter: dateFormatFn,
                },
                {
                    key: 'sponsor',
                    label: 'Sponsor',
                    sortable: true,
                    search: true,
                },
                {
                    key: 'comment',
                    label: 'Comment',
                    sortable: true,
                    search: true,
                    dataFormatter: (value, row) => {
                        const commentStyle = styleMap({
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '180px',
                            whiteSpace: 'nowrap',
                        });

                        return value ? html`<div style=${commentStyle}>${value}</div>` : null;
                    },
                },
                {
                    key: 'updated',
                    label: 'Updated',
                    sortable: true,
                    search: true,
                    dataFormatter: dateFormatFn,
                },
                {
                    key: 'active',
                    label: 'Active',
                    sortable: true,
                    dataFormatter: (value, row) => {
                        const iconStyle = styleMap({
                            color: value ? 'var(--app-primary-color)' : 'rgba(0, 0, 0, 0.1)',
                            textAlign: 'center',
                        });
                        return value
                            ? html`<div style=${iconStyle}>
                                  <tmn-icon
                                      class="user-active"
                                      icon="check_circle_outline"
                                      iconvariant="outlined"></tmn-icon>
                              </div>`
                            : html`<div style=${iconStyle}>
                                  <tmn-icon
                                      class="user-inactive"
                                      icon="circle"
                                      iconvariant="outlined"></tmn-icon>
                              </div>`;
                    },
                },
            ];
        }
    }

    render() {
        const { allusers, _columnDefs, selectedUsersIds, showInactive } = this;
        const selectMode = SELECT_MODES.SINGLE;
        return html`
            <h2>Search all users</h2>
            <div>
                Search checks username and name fields; Search must be at least four characters
                long; 1000 max results
            </div>
            <div class="table-container">
                <tmn-table-selectable
                    @tmn-row-select-toggle=${this.handleRowSelectToggle}
                    @tmn-table-search-changed=${this.handleSearchChanged}
                    .useExternalSearch=${true}
                    .useFilters=${false}
                    .rowIdentifier=${'eSiteId'}
                    .dataRows=${allusers}
                    .selectedRowIds=${selectedUsersIds}
                    .selectMode=${selectMode}
                    .columnDefs=${_columnDefs}>
                    <div slot="right-actions">
                        <tmn-switch
                            .checked=${showInactive}
                            color="primary"
                            @tmn-switch-change=${this.toggleActiveStatus}>
                            Show Inactive
                        </tmn-switch>
                    </div>
                </tmn-table-selectable>
            </div>
        `;
    }

    renderLoading() {
        return html`<loading-logo></loading-logo>`;
    }

    toggleActiveStatus(e) {
        const { showInactive, search } = this;
        this.actions.setAllUserFilters({
            allUserSearch: search,
            showInactive: !showInactive,
        });
    }

    handleSearchChanged(e) {
        this.search = e.detail?.search;
        this.actions.setAllUserFilters({
            allUserSearch: this.search,
            showInactive: this.showInactive,
        });
    }

    handleRowSelectToggle(e) {
        const { selected, rowData } = e.detail;
        const router = getRouter();
        if (selected) {
            router.navigateByName('user-detail', {
                eUserId: rowData.eUserId,
                authSite: rowData.sitename,
            });
        }
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        const filters = selectors.userListFiltersSelector(state);
        const showInactive = filters?.showInactive;
        return {
            allusers: selectors.allUserListSelector(state),
            authorizations: accountSelectors.accountAuthorizationsSelector(state),
            showInactive,
        };
    };
};

customElements.define('user-search-all', connect(mapStateToProps, actions)(UserSearchAll));
